'use client';

import {
  useState,
  type FC,
  type ChangeEvent,
  type FormEvent,
  useContext,
} from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Radio,
  Separator,
  SetEmail,
  SetPassword,
  SetPhoneNumber,
  TwoFactorAuth,
} from '@/_components';
import { LoginContext } from '@/_contexts/LoginProvider';
import type { SetSignInMethodProps } from './SetSignInMethod.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const SetSignInMethod: FC<SetSignInMethodProps> = ({ isStandalone }) => {
  const [value, setValue] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const { preferences } = useContext(FirmSettingsContext);

  const {
    standaloneState,
    setLoginState,
    setStandaloneState,
    setPreviousLoginState,
  } = useContext(LoginContext);

  const onLoginNext = async (event: FormEvent) => {
    event?.preventDefault?.();

    setLoading(true);
    setPreviousLoginState('set-sign-in-method');

    if (value === 'password') return setLoginState('set-password');
    if (value === 'sms') return setLoginState('set-phone-number');
    if (value === 'email') return setLoginState('set-email');
  };

  const onStandaloneNext = (event: FormEvent) => {
    event?.preventDefault?.();

    if (value === 'password') return setStandaloneState('set-password');
    if (value === 'sms') return setStandaloneState('set-phone-number');
    if (value === 'email') return setStandaloneState('set-email');
  };

  const onNext = (event: FormEvent): Promise<void> | void =>
    isStandalone ? onStandaloneNext(event) : onLoginNext(event);

  const onSetValue = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event?.target.value);
  };

  const items = [
    { name: 'I want to sign in using password', value: 'password' },
    ...(preferences?.sms_code_verification_disabled
      ? []
      : [{ name: 'Text me a passcode via SMS', value: 'sms' }]),
    { name: 'Send me a passcode via Email', value: 'email' },
  ];

  const onBackOverride = () => {
    setStandaloneState(undefined);
  };

  switch (standaloneState) {
    case '2fa':
      return (
        <TwoFactorAuth isStandalone={true} onBackOverride={onBackOverride} />
      );
    case 'set-email':
      return <SetEmail onBackOverride={onBackOverride} />;
    case 'set-password':
      return <SetPassword onBackOverride={onBackOverride} />;
    case 'set-phone-number':
      return <SetPhoneNumber onBackOverride={onBackOverride} />;
    default:
      return (
        <form onSubmit={onNext}>
          <Flex isColumn={true} gap={8} padding={10}>
            {items.map((item, key) => (
              <Radio
                key={key}
                label={item.name}
                name="set-phone-number-options"
                onChange={onSetValue}
                selectedValue={value}
                value={item.value}
              />
            ))}
            {isStandalone && <Separator isLight={true} />}
            <ButtonGroup isFullWidth={true}>
              <Button
                value={isStandalone ? 'Save' : 'Next'}
                isLoading={isLoading}
                isDisabled={isLoading || !value}
                type="submit"
                intent="primary"
              />
            </ButtonGroup>
            {!isStandalone && <Separator isLight={true} />}
          </Flex>
        </form>
      );
  }
};
